import { History } from 'lucide-react';

import { formatarDataHora } from '@/lib/formato';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { LinhaDoTempo } from '@/ui/components/linha-do-tempo';
import { type DocumentoDetalhe } from '@fiscoai/contracts';

import { EVENTO } from './rotulos';

type Evento = DocumentoDetalhe['eventos'][number];

const CCE = '110110';
/** Evento que veio só do resumo DF-e (resEvento): sem texto — dizemos isso em vez de esconder. */
const SEM_TEXTO = 'texto indisponível (resumo DF-e)';

/** CC-e: a de maior sequência é a vigente — ela repete todas as correções (Ajuste SINIEF 07/05). */
function tituloDo(e: Evento, ultimaCce: number): string {
  if (e.tipoEvento !== CCE) return EVENTO[e.tipoEvento] ?? e.descricao;
  const estado = e.sequencia === ultimaCce ? 'vigente' : 'substituída';
  return `Carta de correção nº ${String(e.sequencia)} (${estado})`;
}

function descricaoDo(e: Evento): string {
  const protocolo = e.protocolo ? `Protocolo ${e.protocolo}` : null;
  const texto = e.texto ?? (e.origem === 'RESUMO_DFE' ? SEM_TEXTO : null);
  return [texto, protocolo].filter(Boolean).join(' · ');
}

/** Autorização + eventos (cancelamento, CC-e, manifestação) em ordem do tempo. */
export function AbaEventos({ doc }: { doc: DocumentoDetalhe }) {
  const ultimaCce = Math.max(
    0,
    ...doc.eventos.filter((e) => e.tipoEvento === CCE).map((e) => e.sequencia),
  );
  const marcos = [
    ...(doc.autorizacao
      ? [
          {
            id: 'autorizacao',
            titulo: 'Autorizada pela SEFAZ',
            quando: formatarDataHora(doc.autorizacao.recebidoEm),
            descricao: `Protocolo ${doc.autorizacao.protocolo}`,
            tom: 'sucesso' as const,
          },
        ]
      : []),
    ...doc.eventos.map((e) => ({
      id: `${e.tipoEvento}-${String(e.sequencia)}`,
      titulo: tituloDo(e, ultimaCce),
      quando: formatarDataHora(e.ocorridoEm),
      descricao: descricaoDo(e) || undefined,
      tom:
        e.tipoEvento === '110111'
          ? ('perigo' as const)
          : e.tipoEvento === CCE && e.sequencia !== ultimaCce
            ? ('neutro' as const)
            : ('marca' as const),
    })),
  ];
  if (marcos.length === 0) {
    return (
      <EstadoVazio
        icone={<History />}
        titulo="Nenhum evento"
        texto="Quando chegar o XML de um cancelamento ou carta de correção, ele aparece aqui."
      />
    );
  }
  return <LinhaDoTempo marcos={marcos} />;
}
